import { formatDuration } from 'date-fns';

interface Duration {
  years?: number;
  months?: number;
  weeks?: number;
  days?: number;
  hours?: number;
  minutes?: number;
  seconds?: number;
}

/**
 * Parse ISO 8601 duration string into a Duration object
 * Supports formats like: PT1H, P1D, P1W, PT30M, PT1H30M, P1DT2H30M, etc.
 *
 * @param isoDuration - ISO 8601 duration string (e.g., "-PT1H", "P2D", "PT30M")
 * @returns Object with parsed duration and direction
 */
function parseISO8601Duration(isoDuration: string): {
  duration: Duration;
  isNegative: boolean;
} {
  const isNegative = isoDuration.startsWith('-');
  const cleanDuration = isNegative ? isoDuration.slice(1) : isoDuration;

  const duration: Duration = {};

  // ISO 8601 duration regex pattern
  // P[n]Y[n]M[n]DT[n]H[n]M[n]S or P[n]W
  const pattern =
    /^P(?:(\d+(?:\.\d+)?)Y)?(?:(\d+(?:\.\d+)?)M)?(?:(\d+(?:\.\d+)?)W)?(?:(\d+(?:\.\d+)?)D)?(?:T(?:(\d+(?:\.\d+)?)H)?(?:(\d+(?:\.\d+)?)M)?(?:(\d+(?:\.\d+)?)S)?)?$/;

  const match = cleanDuration.match(pattern);

  if (!match) {
    throw new Error(`Invalid ISO 8601 duration: ${isoDuration}`);
  }

  const [, years, months, weeks, days, hours, minutes, seconds] = match;

  if (years) duration.years = parseFloat(years);
  if (months) duration.months = parseFloat(months);
  if (weeks) duration.weeks = parseFloat(weeks);
  if (days) duration.days = parseFloat(days);
  if (hours) duration.hours = parseFloat(hours);
  if (minutes) duration.minutes = parseFloat(minutes);
  if (seconds) duration.seconds = parseFloat(seconds);

  return { duration, isNegative };
}

/**
 * Convert ISO 8601 duration to human-readable format for appointments
 * Uses date-fns formatDuration for robust, localized formatting
 *
 * Examples:
 * "-PT1H" → "1 hour before appointment"
 * "-P2D" → "2 days before appointment"
 * "PT30M" → "30 minutes after appointment"
 * "-P1DT2H" → "1 day 2 hours before appointment"
 * "-PT0M" or "PT0M" → "at appointment time"
 *
 * @param isoDuration - ISO 8601 duration string
 * @returns Human-readable duration string with appointment context
 */
export function formatAppointmentDuration(isoDuration: string): string {
  if (!isoDuration || isoDuration === '0' || isoDuration === 'PT0M' || isoDuration === 'PT0H') {
    return 'at appointment time';
  }

  try {
    const { duration, isNegative } = parseISO8601Duration(isoDuration);

    // Check if duration is essentially zero
    const hasNonZeroValue = Object.values(duration).some(value => value && value > 0);
    if (!hasNonZeroValue) {
      return 'at appointment time';
    }

    // Use date-fns formatDuration for clean, localized formatting
    const formattedDuration = formatDuration(duration, {
      format: getOptimalFormat(duration),
      delimiter: ' ',
    });

    if (!formattedDuration) {
      return 'at appointment time';
    }

    const direction = isNegative ? 'before' : 'after';
    return `${formattedDuration} ${direction} appointment`;
  } catch (error) {
    console.warn('Failed to parse ISO 8601 duration:', isoDuration, error);
    return isoDuration; // Fallback to original string
  }
}

/**
 * Determine the optimal format array for formatDuration based on the duration values
 * This helps avoid showing unnecessary zero values and prioritizes the most significant units
 */
function getOptimalFormat(duration: Duration): (keyof Duration)[] {
  const format: (keyof Duration)[] = [];

  // Add units in order of significance, but only if they have values or are needed for context
  if (duration.years) format.push('years');
  if (duration.months) format.push('months');
  if (duration.weeks) format.push('weeks');
  if (duration.days) format.push('days');
  if (duration.hours) format.push('hours');
  if (duration.minutes) format.push('minutes');
  if (duration.seconds) format.push('seconds');

  // If no specific format determined, default to a reasonable subset
  if (format.length === 0) {
    return ['days', 'hours', 'minutes'];
  }

  // Limit to 2-3 most significant units for readability
  return format.slice(0, 3);
}

/**
 * Batch convert multiple ISO 8601 durations to human-readable format
 * Useful for processing lists of appointments, forms, or reminders
 */
export function formatAppointmentDurations(isoDurations: string[]): string[] {
  return isoDurations.map(formatAppointmentDuration);
}

/**
 * Check if an ISO 8601 duration represents "immediate" timing (zero duration)
 */
export function isImmediateTiming(isoDuration: string): boolean {
  if (!isoDuration || isoDuration === '0') return true;

  try {
    const { duration } = parseISO8601Duration(isoDuration);
    return !Object.values(duration).some(value => value && value > 0);
  } catch {
    return false;
  }
}

/**
 * Get the direction of a duration (before/after appointment)
 */
export function getDurationDirection(isoDuration: string): 'before' | 'after' | 'immediate' {
  if (isImmediateTiming(isoDuration)) return 'immediate';
  return isoDuration.startsWith('-') ? 'before' : 'after';
}
